/**
 * Per-app run log client (#184) — a thin wrapper over the permission-gated,
 * READ-ONLY `/api/app-run-log/:id` route.
 *
 * Two methods, and the missing third is the point. There is `get` and `clear`;
 * there is deliberately NO `append`.
 *
 * This docblock used to say there was one method and there must stay one, on the
 * grounds that no-write-route WAS the tamper guarantee. That was sharper than
 * the threat: #184 names the app's own untrusted SCRIPTS, and a script reaches
 * neither of these — both go through the session-authenticated, `readwrite`-gated
 * route, and nothing in the SDK or the `papan:` relay can call them. What holds
 * is that the APP cannot edit its own history while a person with edit rights can
 * clear theirs.
 *
 * `append` stays absent because an app that could write here could FORGE its
 * history, which is the half of the criterion that has not moved.
 */
import { request } from '@papan/api-client'

/** What the platform observed. Free text on the wire — the server's column is
 *  deliberately unconstrained so a new kind is a code change, not a migration —
 *  so the UI must render an unknown value rather than assuming this union is
 *  exhaustive. */
export type AppRunEventKind =
  | 'sandbox_acquire_failed'
  | 'sandbox_exec_failed'
  | 'script_error'
  | 'timeout'
  | 'too_many_concurrent_runs'
  | 'app_events_dropped'
  | 'db_run_stderr_ignored'
  | 'output_truncated'
  | 'post_exec_failed'
  | 'file_too_large'
  | 'files_not_saved'
  | 'hook_gave_up'
  | 'run_lock_lost'
  | 'db_writes_failed'
  | 'allowance_spent'

export interface AppRunEvent {
  id: string
  /** See `AppRunEventKind` — may be a value this client does not know. */
  kind: string
  /** The code the CALLER was given, when there was one. Distinct from `kind`:
   *  a caller sees `sandbox_exec_failed` for both a genuine exec failure and an
   *  exhausted vanish-retry, which are different diagnoses. */
  errorCode: string | null
  /** `null` = the script never ran. NEVER read as 0. */
  exitCode: number | null
  durationMs: number | null
  /** 'user' (a synchronous scripts.run) or 'app_hook' (the scheduled lane). */
  trigger: string
  scriptPath: string | null
  stderrTail: string | null
  correlationId: string | null
  /** The caller's raw user agent; rendered via `describeUa` as
   *  "Chrome 126 · macOS", never inline raw. `null` for the UNATTENDED app-hook
   *  lane, which has no browser — an absence, never a placeholder. */
  userAgent: string | null
  /** Epoch ms. */
  at: number
}

export interface AppRunLogResult {
  runs: AppRunEvent[]
  /** The page was capped — older runs exist that this response does not carry.
   *  Stated by the server rather than inferred, because a full page is otherwise
   *  indistinguishable from an app that had exactly that many failures. */
  truncated: boolean
}

export const appRunLogApi = {
  /**
   * This app's recorded failures, newest first.
   *
   * `id` may be the app folder or any file inside it; the server resolves it to
   * the folder and gates on THAT — a run-log row belongs to the app, not to one
   * file. Throws on 403 (no write access on the folder).
   */
  async get(id: string, limit?: number): Promise<AppRunLogResult> {
    const qs = limit !== undefined ? `?limit=${limit}` : ''
    const res = await request<AppRunLogResult>(`/api/app-run-log/${encodeURIComponent(id)}${qs}`)
    return { runs: res.runs ?? [], truncated: res.truncated ?? false }
  },

  /**
   * Clear this app's recorded failures, or only the last `withinMs` of them.
   *
   * Returns how many rows went, so the caller can say so rather than guessing.
   * The operator's own record (`sandbox_start_failed`, `oauth_audit_log`) is
   * untouched and still joinable by the correlation id — this clears the
   * author's view, not the platform's evidence.
   */
  async clear(id: string, withinMs?: number): Promise<{ cleared: number }> {
    const qs = withinMs !== undefined ? `?withinMs=${withinMs}` : ''
    const res = await request<{ cleared?: number }>(
      `/api/app-run-log/${encodeURIComponent(id)}${qs}`,
      { method: 'DELETE' },
    )
    return { cleared: res.cleared ?? 0 }
  },

  /** Each run source's last write outcome for one app (its triggers, schedules and scripts). Throws on 403. */
  async writes(id: string): Promise<AppRunWritesResult> {
    const res = await request<AppRunWritesResult>(`/api/app-run-log/${encodeURIComponent(id)}/writes`)
    return { sources: res.sources ?? [] }
  },

  /** How many run sources of each app have an undismissed write failure — only apps the caller can edit are answered
   *  (any other id is absent, i.e. nothing to show). */
  async writeFailureCounts(ids: readonly string[]): Promise<Record<string, number>> {
    if (!ids.length) return {}
    const res = await request<{ counts?: Record<string, number> }>(`/api/app-run-log/write-failures?ids=${ids.map(encodeURIComponent).join(',')}`)
    return res.counts ?? {}
  },

  /** Dismiss one source's failure (`runKey`) or every one of the app's. */
  async dismissWriteFailure(id: string, runKey?: string): Promise<{ dismissed: number }> {
    const res = await request<{ dismissed?: number }>(`/api/app-run-log/${encodeURIComponent(id)}/writes/dismiss`, {
      method: 'POST',
      body: JSON.stringify(runKey ? { runKey } : {}),
      headers: { 'Content-Type': 'application/json' },
    })
    return { dismissed: res.dismissed ?? 0 }
  },
}

/** One run source's last write outcome (`app_run_writes`). `runKey`: `trigger:<id>` · `schedule:<id>` · `hook:<script>` ·
 *  `script:<path>` (`appRunKey`, @papan/types). */
export interface AppRunWriteSource {
  runKey: string
  runId: string
  /** 'landed': all saved · 'partial': saved except single calls the database refused (bad data — redoing repeats it) ·
   *  'failed': the run's writes (or the rest of them) were not applied. Both non-landed ones need an editor's look. */
  outcome: 'landed' | 'partial' | 'failed'
  /** Why it failed, in the author's words. */
  reason: string | null
  scriptPath: string | null
  /** How long applying the writes took (ms); null when nothing was applied. */
  writeMs: number | null
  slowestOps: Array<{ op: string; ms: number; count: number }>
  /** Epoch ms. */
  at: number
  dismissed: boolean
}

export interface AppRunWritesResult {
  sources: AppRunWriteSource[]
}
